import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Ticket, Trophy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Performer } from "@shared/schema";

interface SetRaffleEntrancesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  performer: Performer | null;
  jamNightId: number;
}

export default function SetRaffleEntrancesModal({
  open,
  onOpenChange,
  performer,
  jamNightId,
}: SetRaffleEntrancesModalProps) {
  const [entrances, setEntrances] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Reset form when modal opens or performer changes
  useEffect(() => {
    if (open && performer) {
      setEntrances((performer.raffleEntrances || 0).toString());
    }
  }, [open, performer]);

  const updateEntrancesMutation = useMutation({
    mutationFn: async (newEntrances: number) => {
      if (!performer) return;
      return apiRequest("PATCH", `/api/performers/${performer.id}`, {
        raffleEntrances: newEntrances
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      toast({
        title: "Success",
        description: "Raffle entrances updated successfully",
      });
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update raffle entrances",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const entranceCount = parseInt(entrances);
    if (isNaN(entranceCount) || entranceCount < 0) {
      toast({
        title: "Invalid input",
        description: "Please enter a valid number (0 or higher)",
        variant: "destructive",
      });
      return;
    }

    updateEntrancesMutation.mutate(entranceCount);
  };

  const handleClose = () => {
    setEntrances("");
    onOpenChange(false);
  };

  if (!performer) return null;

  const currentWins = performer.raffleWins || 0;
  const currentEntrances = performer.raffleEntrances || 0;
  const effectiveEntrances = Math.max(0, currentEntrances - currentWins);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5 text-yellow-500" />
            Set Raffle Entrances - {performer.name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Status */}
          <div className="space-y-3">
            <div className="text-sm text-slate-600">Current Status:</div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="text-xs">
                <Ticket className="h-3 w-3 mr-1" />
                {currentEntrances} total entrances
              </Badge>
              {currentWins > 0 && (
                <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-300">
                  <Trophy className="h-3 w-3 mr-1" />
                  {currentWins} wins
                </Badge>
              )}
              <Badge 
                variant={effectiveEntrances > 0 ? "default" : "secondary"} 
                className={`text-xs ${effectiveEntrances > 0 ? "bg-green-600" : "bg-gray-500"}`}
              >
                {effectiveEntrances} effective entries
              </Badge>
            </div>
            <div className="text-xs text-slate-500">
              Effective entries = Total entrances - Wins (tickets are removed when they win)
            </div>
          </div>

          {/* Input Field */}
          <div className="space-y-2">
            <Label htmlFor="entrances">Number of Raffle Entrances</Label>
            <Input
              id="entrances"
              type="number"
              min="0"
              value={entrances}
              onChange={(e) => setEntrances(e.target.value)}
              placeholder="Enter number of entrances"
              className="text-center"
              autoFocus
            />
            <div className="text-xs text-slate-500">
              Set to 0 to remove from raffle. Each entrance is like a raffle ticket.
            </div>
          </div>

          {/* Preview */}
          {entrances !== "" && !isNaN(parseInt(entrances)) && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-sm font-medium text-slate-700 mb-2">Preview:</div>
              <div className="text-xs text-slate-600">
                {parseInt(entrances)} total entrances - {currentWins} wins = {" "}
                <span className="font-medium text-green-600">
                  {Math.max(0, parseInt(entrances) - currentWins)} effective entries
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={updateEntrancesMutation.isPending}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {updateEntrancesMutation.isPending ? "Updating..." : "Update Entrances"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}